const BASE_URL = 'https://api.vectorvault.io/';

// Temporary mock data for testing (remove when backend is ready)
const getMockFlowRuntimes = () => {
  return {
    status: 'success',
    flow_runtimes: [
      {
        runtime_id: 'mock_runtime_1',
        flow_name: 'Customer Support Agent',
        status: 'active',
        current_node_name: 'Process Inquiry',
        current_node_id: 'node_123',
        invoke_method: 'API Call',
        should_stop: false,
        is_parent_flow: true,
        total_duration_seconds: 145,
        node_duration_seconds: 23,
        total_duration_formatted: '2m 25s',
        node_duration_formatted: '23s',
        started_at: Date.now() / 1000 - 145,
        current_node_started_at: Date.now() / 1000 - 23,
        message: 'Processing customer inquiry about product availability...'
      },
      {
        runtime_id: 'mock_runtime_2',
        flow_name: 'Data Processing Pipeline',
        status: 'completed',
        current_node_name: 'Final Output',
        current_node_id: 'node_456',
        invoke_method: 'Scheduled',
        should_stop: false,
        is_parent_flow: true,
        total_duration_seconds: 890,
        node_duration_seconds: 0,
        total_duration_formatted: '14m 50s',
        node_duration_formatted: 'Final',
        started_at: Date.now() / 1000 - 1200,
        current_node_started_at: Date.now() / 1000 - 1200,
        completion_response: 'Successfully processed 1,250 records and generated analytics report.'
      },
      {
        runtime_id: 'mock_runtime_3',
        flow_name: 'Email Automation',
        status: 'stopped',
        current_node_name: 'Send Email',
        current_node_id: 'node_789',
        invoke_method: 'Manual',
        should_stop: true,
        is_parent_flow: false,
        total_duration_seconds: 67,
        node_duration_seconds: 0,
        total_duration_formatted: '1m 7s',
        node_duration_formatted: 'Final',
        started_at: Date.now() / 1000 - 300,
        current_node_started_at: Date.now() / 1000 - 300,
        message: 'Stopped due to API rate limit exceeded.'
      }
    ],
    total_active: 1,
    total_stopping: 0
  };
};

const getMockFlowStats = () => {
  return {
    status: 'success',
    stats: {
      total_active: 1,
      total_stopping: 0,
      total_parent_flows: 2,
      total_child_flows: 1,
      longest_running_duration: 145,
      longest_running_flow: {
        runtime_id: 'mock_runtime_1',
        flow_name: 'Customer Support Agent',
        duration_formatted: '2m 25s'
      },
      flows_by_invoke_method: {
        'API Call': 1,
        'Scheduled': 1,
        'Manual': 1
      },
      flows_by_status: {
        'running': 1,
        'stopping': 0
      },
      average_runtime: 367,
      average_runtime_formatted: '6m 7s',
      longest_running_duration_formatted: '2m 25s'
    }
  };
};

// Get all active flow runtimes for dashboard
export const getFlowRuntimes = async (vectorVault) => {
  try {
    const response = await vectorVault.makeAuthenticatedRequest(`${BASE_URL}dashboard/flow-runtimes`, {
      method: 'GET'
    });
    
    if (!response.ok) {
      // Return mock data if backend endpoint doesn't exist (422/404 errors)
      if (response.status === 422 || response.status === 404) {
        return getMockFlowRuntimes();
      }
      
      const errorText = await response.text();
      throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
    }
    
    return await response.json();
  } catch (error) {
    // If it's a network error or endpoint doesn't exist, use mock data
    if (error.message.includes('422') || error.message.includes('404')) {
      return getMockFlowRuntimes();
    }
    console.error('Error fetching flow runtimes:', error);
    throw error;
  }
};

// Get flow dashboard statistics
export const getFlowStats = async (vectorVault) => {
  try {
    const response = await vectorVault.makeAuthenticatedRequest(`${BASE_URL}dashboard/flow-stats`, {
      method: 'GET'
    });
    
    if (!response.ok) {
      // Return mock data if backend endpoint doesn't exist (422/404 errors)
      if (response.status === 422 || response.status === 404) {
        return getMockFlowStats();
      }
      
      const errorText = await response.text();
      throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
    }
    
    return await response.json();
  } catch (error) {
    // If it's a network error or endpoint doesn't exist, use mock data
    if (error.message.includes('422') || error.message.includes('404')) {
      return getMockFlowStats();
    }
    console.error('Error fetching flow stats:', error);
    throw error;
  }
};

// Get detailed information about a specific flow runtime
export const getFlowRuntimeDetails = async (runtimeId, vectorVault) => {
  try {
    const response = await vectorVault.makeAuthenticatedRequest(`${BASE_URL}dashboard/flow-runtime/${runtimeId}`, {
      method: 'GET'
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error fetching flow runtime details:', error);
    throw error;
  }
};

// Stop a specific flow runtime
export const stopFlowRuntime = async (runtimeId, vectorVault) => {
  try {
    const response = await vectorVault.makeAuthenticatedRequest(`${BASE_URL}dashboard/flow-runtime/${runtimeId}/stop`, {
      method: 'POST'
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error stopping flow runtime:', error);
    throw error;
  }
};

// Force delete a flow runtime (emergency cleanup)
export const forceDeleteFlowRuntime = async (runtimeId, vectorVault) => {
  try {
    const response = await vectorVault.makeAuthenticatedRequest(`${BASE_URL}dashboard/flow-runtime/${runtimeId}/force-delete`, {
      method: 'DELETE'
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error force deleting flow runtime:', error);
    throw error;
  }
};

// Stop multiple flow runtimes at once
export const bulkStopFlows = async (runtimeIds, vectorVault) => {
  try {
    const response = await vectorVault.makeAuthenticatedRequest(`${BASE_URL}dashboard/bulk-stop-flows`, {
      method: 'POST',
      body: JSON.stringify({ runtime_ids: runtimeIds })
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error bulk stopping flows:', error);
    throw error;
  }
};

// Get logs for a specific flow runtime
export const getFlowRuntimeLogs = async (runtimeId, vectorVault) => {
  try {
    const response = await vectorVault.makeAuthenticatedRequest(`${BASE_URL}dashboard/flow-runtime/${runtimeId}/logs`, {
      method: 'GET'
    });
    
    if (!response.ok) {
      // Return mock logs for testing
      if (response.status === 422 || response.status === 404) {
        return {
          status: 'success',
          runtime_id: runtimeId,
          flow_name: 'Mock Flow',
          logs: [
            {
              id: 'log_1',
              message: 'Flow started successfully',
              timestamp: Date.now() / 1000 - 120,
              formatted_time: new Date(Date.now() - 120000).toLocaleString()
            },
            {
              id: 'log_2', 
              message: 'Processing user input...',
              timestamp: Date.now() / 1000 - 60,
              formatted_time: new Date(Date.now() - 60000).toLocaleString()
            },
            {
              id: 'log_3',
              message: 'Current operation in progress',
              timestamp: Date.now() / 1000 - 10,
              formatted_time: new Date(Date.now() - 10000).toLocaleString()
            }
          ],
          total_logs: 3
        };
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error fetching flow runtime logs:', error);
    throw error;
  }
};

// Clear logs for a specific flow runtime
export const clearFlowRuntimeLogs = async (runtimeId, vectorVault) => {
  try {
    const response = await vectorVault.makeAuthenticatedRequest(`${BASE_URL}dashboard/flow-runtime/${runtimeId}/logs/clear`, {
      method: 'DELETE'
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error clearing flow runtime logs:', error);
    throw error;
  }
};

// Cleanup old logs
export const cleanupOldLogs = async (vectorVault) => {
  try {
    const response = await vectorVault.makeAuthenticatedRequest(`${BASE_URL}dashboard/logs/cleanup`, {
      method: 'POST'
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error cleaning up old logs:', error);
    throw error;
  }
}; 